import { useState } from 'react';
import { X } from 'lucide-react';
import { AI_SYSTEMS, AISystem } from '../../config/ai';
import { DEFAULT_PROMPTS } from '../../services/promptLibrary';

interface AuditConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (config: AuditConfigData) => void;
}

export interface AuditConfigData {
  brandName: string;
  aiSystems: AISystem[];
  selectedPrompts: number[];
  variables: Record<string, string>;
}

export function AuditConfigModal({ isOpen, onClose, onSubmit }: AuditConfigModalProps) {
  const [brandName, setBrandName] = useState('Acme Corp');
  const [selectedSystems, setSelectedSystems] = useState<AISystem[]>(['gemini']);
  const [selectedPrompts, setSelectedPrompts] = useState<number[]>(
    [0, 1, 2, 3, 4] // First 5 prompts
  );
  const [variables, setVariables] = useState<Record<string, string>>({
    product_category: 'project management software',
    buyer_type: 'startups',
    use_case: 'agile development',
  });

  if (!isOpen) return null;

  const handleSystemToggle = (systemId: AISystem) => {
    setSelectedSystems((prev) =>
      prev.includes(systemId)
        ? prev.filter((s) => s !== systemId)
        : [...prev, systemId]
    );
  };

  const handlePromptToggle = (promptIndex: number) => {
    setSelectedPrompts((prev) =>
      prev.includes(promptIndex)
        ? prev.filter((p) => p !== promptIndex)
        : [...prev, promptIndex]
    );
  };

  const handleSubmit = () => {
    onSubmit({
      brandName,
      aiSystems: selectedSystems,
      selectedPrompts,
      variables,
    });
    onClose();
  };

  const enabledSystems = AI_SYSTEMS.filter((s) => s.enabled);
  const totalQueries = selectedSystems.length * selectedPrompts.length * 3;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Configure Audit Run</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Brand Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Name
            </label>
            <input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              placeholder="Your Company Name"
            />
          </div>

          {/* AI Systems */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AI Systems to Query
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Select which AI platforms to test your brand visibility across
            </p>
            <div className="grid grid-cols-2 gap-3">
              {AI_SYSTEMS.map((system) => (
                <label
                  key={system.id}
                  className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                    system.enabled
                      ? selectedSystems.includes(system.id)
                        ? 'border-primary-500 bg-primary-50 cursor-pointer'
                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                      : 'border-gray-200 border-dashed bg-gray-50 cursor-not-allowed opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSystems.includes(system.id)}
                    onChange={() => system.enabled && handleSystemToggle(system.id)}
                    disabled={!system.enabled}
                    className="w-5 h-5 text-primary-500 rounded focus:ring-primary-500 disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900 block">{system.name}</span>
                    {!system.enabled && (
                      <span className="text-xs text-gray-500">API key required</span>
                    )}
                  </div>
                </label>
              ))}
            </div>
            {enabledSystems.length < AI_SYSTEMS.length && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Enable {AI_SYSTEMS.length - enabledSystems.length} more AI systems:</strong> Add API keys to your <code className="bg-blue-100 px-1 rounded">.env</code> file to query {AI_SYSTEMS.filter(s => !s.enabled).map(s => s.name.split(' ')[0]).join(', ')}.
                </p>
              </div>
            )}
          </div>

          {/* Prompts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Prompts to Run ({selectedPrompts.length} selected)
            </label>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
              {DEFAULT_PROMPTS.map((prompt, index) => (
                <label
                  key={index}
                  className="flex items-start gap-3 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedPrompts.includes(index)}
                    onChange={() => handlePromptToggle(index)}
                    className="mt-1 w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">{prompt.template}</p>
                    <p className="text-xs text-gray-500 mt-1">Category: {prompt.category}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Variables */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Variables
            </label>
            <div className="space-y-3">
              {Object.entries(variables).map(([key, value]) => (
                <div key={key} className="flex gap-3">
                  <input
                    type="text"
                    value={key}
                    readOnly
                    className="w-1/3 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono"
                  />
                  <input
                    type="text"
                    value={value}
                    onChange={(e) =>
                      setVariables({ ...variables, [key]: e.target.value })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Value"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <p className="text-sm font-medium text-primary-900 mb-2">Audit Summary</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-primary-700">AI Systems</p>
                <p className="font-bold text-primary-900">{selectedSystems.length}</p>
              </div>
              <div>
                <p className="text-primary-700">Prompts</p>
                <p className="font-bold text-primary-900">{selectedPrompts.length}</p>
              </div>
              <div>
                <p className="text-primary-700">Total Queries</p>
                <p className="font-bold text-primary-900">{totalQueries}</p>
              </div>
            </div>
            <p className="text-xs text-primary-700 mt-3">
              Each prompt runs 3x per AI system for median scoring
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedSystems.length === 0 || selectedPrompts.length === 0}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Run Audit ({totalQueries} queries)
          </button>
        </div>
      </div>
    </div>
  );
}
